import { useState, useEffect } from "react";
import { X, Download, Smartphone, Share, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";

export function InstallPWA() {
  const [showPrompt, setShowPrompt] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isIOS, setIsIOS] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    // Check if already installed (standalone mode)
    const standalone = window.matchMedia('(display-mode: standalone)').matches 
      || window.navigator.standalone === true;
    setIsStandalone(standalone);

    // Check if dismissed recently (24h)
    const dismissedAt = localStorage.getItem('pwa-prompt-dismissed');
    if (dismissedAt) {
      const hoursSinceDismissed = (Date.now() - parseInt(dismissedAt)) / (1000 * 60 * 60);
      if (hoursSinceDismissed < 24) {
        setDismissed(true);
      }
    }

    // Detect iOS
    const iOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    setIsIOS(iOS);

    // For Android/Chrome - listen for beforeinstallprompt
    const handleBeforeInstall = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      // Show prompt after 3 seconds on the page
      setTimeout(() => {
        if (!standalone && !dismissed) {
          setShowPrompt(true);
        }
      }, 3000);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstall);

    // For iOS - show manual instructions after delay
    if (iOS && !standalone && !dismissed) {
      setTimeout(() => setShowPrompt(true), 5000);
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
    };
  }, [dismissed]);

  const handleInstall = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === 'accepted') {
        setShowPrompt(false);
      }
      setDeferredPrompt(null);
    }
  };

  const handleDismiss = () => {
    localStorage.setItem('pwa-prompt-dismissed', Date.now().toString());
    setShowPrompt(false);
    setDismissed(true);
  };

  // Don't show if already installed, dismissed, or on desktop
  if (isStandalone || dismissed || !showPrompt) {
    return null;
  }

  return (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 animate-in fade-in duration-300"
        onClick={handleDismiss}
      />
      
      {/* Bottom Sheet */}
      <div className="fixed bottom-0 left-0 right-0 z-50 animate-in slide-in-from-bottom duration-300">
        <div className="bg-white dark:bg-slate-900 rounded-t-3xl shadow-2xl p-6 pb-8 mx-auto max-w-lg">
          {/* Handle bar */}
          <div className="w-12 h-1.5 bg-slate-300 dark:bg-slate-600 rounded-full mx-auto mb-4" />
          
          {/* Close button */}
          <button 
            onClick={handleDismiss}
            className="absolute top-4 right-4 p-2 rounded-full hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
          >
            <X className="w-5 h-5 text-slate-500" />
          </button>

          {/* Icon */}
          <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-gradient-to-br from-[#16233B] to-[#2d5a8a] flex items-center justify-center shadow-lg">
            <Smartphone className="w-8 h-8 text-white" />
          </div>

          {/* Title */}
          <h3 className="text-xl font-bold text-center text-slate-900 dark:text-white mb-2">
            Installe Odys'<span className="text-[#F5B731]">IA</span>
          </h3>

          {/* Description */}
          <p className="text-center text-slate-600 dark:text-slate-400 mb-6 text-sm">
            Accede a tes experts immobiliers directement depuis ton ecran d'accueil. Plus rapide, plus pratique !
          </p>

          {/* Benefits */}
          <div className="grid grid-cols-3 gap-3 mb-6">
            <div className="text-center p-3 rounded-xl bg-slate-50 dark:bg-slate-800">
              <div className="text-2xl mb-1">⚡</div>
              <p className="text-xs text-slate-600 dark:text-slate-400">Acces rapide</p>
            </div>
            <div className="text-center p-3 rounded-xl bg-slate-50 dark:bg-slate-800">
              <div className="text-2xl mb-1">📱</div>
              <p className="text-xs text-slate-600 dark:text-slate-400">Mode plein ecran</p>
            </div>
            <div className="text-center p-3 rounded-xl bg-slate-50 dark:bg-slate-800">
              <div className="text-2xl mb-1">🔔</div>
              <p className="text-xs text-slate-600 dark:text-slate-400">Notifications</p>
            </div>
          </div>

          {isIOS ? (
            /* iOS Instructions */
            <div className="space-y-4">
              <div className="flex items-center gap-3 p-3 rounded-xl bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800">
                <div className="w-10 h-10 rounded-full bg-blue-500 flex items-center justify-center flex-shrink-0">
                  <Share className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="font-medium text-slate-900 dark:text-white text-sm">1. Appuie sur Partager</p>
                  <p className="text-xs text-slate-500 dark:text-slate-400">En bas de Safari</p>
                </div>
              </div>
              
              <div className="flex items-center gap-3 p-3 rounded-xl bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800">
                <div className="w-10 h-10 rounded-full bg-green-500 flex items-center justify-center flex-shrink-0">
                  <Plus className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="font-medium text-slate-900 dark:text-white text-sm">2. "Sur l'ecran d'accueil"</p>
                  <p className="text-xs text-slate-500 dark:text-slate-400">Puis "Ajouter"</p>
                </div>
              </div>

              <Button 
                onClick={handleDismiss}
                className="w-full bg-[#16233B] hover:bg-[#2d5a8a] text-white py-6"
              >
                J'ai compris
              </Button>
            </div>
          ) : (
            /* Android/Chrome Install Button */
            <div className="space-y-3">
              <Button 
                onClick={handleInstall}
                className="w-full bg-[#16233B] hover:bg-[#2d5a8a] text-white py-6 text-base font-semibold"
              >
                <Download className="w-5 h-5 mr-2" />
                Installer l'application
              </Button>
              
              <button 
                onClick={handleDismiss}
                className="w-full text-center text-sm text-slate-500 hover:text-slate-700 dark:hover:text-slate-300 py-2"
              >
                Plus tard
              </button>
            </div>
          )}
        </div>
      </div>
    </>
  );
}

// Hook to manually trigger install prompt
export function useInstallPWA() {
  const [canInstall, setCanInstall] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);

  useEffect(() => {
    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setCanInstall(true);
    };

    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const install = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      setDeferredPrompt(null);
      setCanInstall(false);
      return outcome === 'accepted';
    }
    return false;
  };

  return { canInstall, install };
}
